import "./case-summary-accordion.scss";
import { HTMLProps, useRef } from "react";
import { P2Accordion, P2TextDescription, P2TextLabel } from "react-bootstrap-common-script";
import { Separator } from "../../../../separator";
import { Helpers } from "../../../../../utils/helper";
import { ApplicationDetailsModal, ApplicationDetailsModalHandle } from "../application-details-modal/application-details-modal";
import { OwnerTable } from "../owner-table/owner-table";
import { PenIcon } from "../../../../../assets/svg";
import { DateModal, DateModalHandle } from "../date-modal/date-modal";
import { EmailModal, EmailModalHandle } from "../email-modal/email-modal";
import { CooSubmission } from "../../../../../models/classes/CooSubmission.class";
import { ChangeOwnershipReasonCode } from "../../../../../utils/code-converter";
import { useSolicitorCaseDetailsContext } from "../../../../../context/solicitor-case-details-context";
import { ApplicationStatus } from "../application-status/application-status";

interface CaseSummaryAccordionProps extends HTMLProps<HTMLDivElement> {
  applicationDetail?: CooSubmission;
}

export function CaseSummaryAccordion({ ...props }: Readonly<CaseSummaryAccordionProps>) {
  const queryParameters = new URLSearchParams(window.location.search);

  // States
  const { solicitorCaseSummaryData, solicitorCaseSummaryIsFetching, isCancelledOrRejected, solicitorCompletionData } = useSolicitorCaseDetailsContext();

  // Refs
  const accordionRef = useRef<HTMLDivElement>(null);
  const applicationDetailsModalRef = useRef<ApplicationDetailsModalHandle>(null);
  const dateModalRef = useRef<DateModalHandle>(null);
  const emailModalRef = useRef<EmailModalHandle>(null);

  // Derived values
  const changeOwnershipDetails = ChangeOwnershipReasonCode.find((obj) => obj.code === props.applicationDetail?.change_ownership_reason);
  const { label } = changeOwnershipDetails ?? { label: "" };
  const scheduledCompletionDate = solicitorCaseSummaryData?.scheduled_completion_date ?? solicitorCaseSummaryData?.tentative_completion_date;
  const alternativeEmailAddress = solicitorCaseSummaryData?.alternative_email_address ?? "";

  // Functions
  function handleOpenApplicationDetailsModal() {
    applicationDetailsModalRef.current?.setIsShowing(true);
  }

  function handleOpenDateModal() {
    dateModalRef.current?.setIsShowing(true);
  }

  function handleOpenEmailModal() {
    emailModalRef.current?.setIsShowing(true);
  }

  if (!solicitorCaseSummaryIsFetching && !solicitorCaseSummaryData) return null;

  return (
    <>
      <div className="polymer-case-summary-accordion" {...props}>
        <P2Accordion ref={accordionRef} defaultOpen={false} title={<P2TextDescription color="black" title="Case Summary" />}>
          <div className="polymer-case-summary-accordion__application-table">
            <div className="polymer-case-summary-accordion__application-table-row index">Application status</div>
            <div className="polymer-case-summary-accordion__application-table-row">
              {solicitorCaseSummaryData ? <ApplicationStatus status={solicitorCaseSummaryData?.application_status} /> : null}
            </div>

            <div className="polymer-case-summary-accordion__application-table-row index">Reason</div>
            <div className="polymer-case-summary-accordion__application-table-row">
              <p style={{ marginBottom: 8 }}>{label}</p>
              <button className="polymer-case-summary-accordion__button" onClick={handleOpenApplicationDetailsModal}>
                <span>View application details</span>
                <i className="bi bi-chevron-right" style={{ fontSize: 16, display: "flex", alignSelf: "center" }} />
              </button>
            </div>

            {!isCancelledOrRejected && solicitorCompletionData?.can_view ? (
              <>
                <P2TextLabel
                  className="polymer-case-summary-accordion__application-table-row index"
                  label="Scheduled completion date"
                  hasTooltip={true}
                  tooltipContent={
                    <>
                      <p className="polymer-case-summary-accordion__tooltip-title">Schedule completion date</p>
                      <p className="polymer-case-summary-accordion__tooltip-content">
                        Please ensure the scheduled completion date is updated accordingly. A change in the scheduled completion date will generate a new financial plan.
                      </p>
                    </>
                  }
                />
                <div className="polymer-case-summary-accordion__application-table-row">
                  <button className="polymer-case-summary-accordion__button" disabled={!solicitorCompletionData?.can_edit} onClick={handleOpenDateModal}>
                    <span>{scheduledCompletionDate ? Helpers.formatDate(scheduledCompletionDate, "short") : "Add appointment date"}</span>
                    <PenIcon fillColor={solicitorCompletionData?.can_edit ? "#0A676D" : "#AAAAAA"} />
                  </button>
                </div>
              </>
            ) : null}

            {!isCancelledOrRejected ? (
              <>
                <div className="polymer-case-summary-accordion__application-table-row index">Alternate email address</div>
                <div className="polymer-case-summary-accordion__application-table-row">
                  <button className="polymer-case-summary-accordion__button" onClick={handleOpenEmailModal}>
                    <span>{alternativeEmailAddress ? alternativeEmailAddress : "Add email address"}</span>
                    <PenIcon />
                  </button>
                </div>
              </>
            ) : null}
          </div>

          <Separator variant="solid" width={1} />

          <div className="polymer-case-summary-accordion__owner-table">
            <OwnerTable itemsToShow={["owners", "loan", "lawyer"]} />
          </div>
        </P2Accordion>
      </div>
      <ApplicationDetailsModal ref={applicationDetailsModalRef} />
      <DateModal ref={dateModalRef} />
      <EmailModal ref={emailModalRef} />
    </>
  );
}
